import { useState } from 'react'

import { useMeasurements } from '../../hooks/useMeasurements'
import {
  formatMeasurement,
  formatMeasurementInputValue,
  inputStepFor,
  labelWithUnit,
  normalizeMeasurementUnit,
  parseMeasurementInput,
  unitLabelFor,
  type MeasurementKind,
  type MeasurementUnit
} from '../../lib/measurements'
import { cn } from '../../utils/cn'
import { Input, type InputProps } from './input'

type UnitOverrideProps = {
  unit?: MeasurementUnit
}

export type MeasurementValueProps = UnitOverrideProps & {
  kind: MeasurementKind
  valueMetric?: number | null
  className?: string
  empty?: string
  fractionDigits?: number
}

export const MeasurementValue = ({
  kind,
  valueMetric,
  className,
  empty,
  fractionDigits,
  unit: unitOverride
}: MeasurementValueProps) => {
  const measurements = useMeasurements()
  const unit = normalizeMeasurementUnit(unitOverride ?? measurements.unit)

  return (
    <span className={className}>
      {formatMeasurement(valueMetric, kind, unit, { empty, fractionDigits })}
    </span>
  )
}

export type MeasurementInputProps = Omit<InputProps, 'value' | 'onChange' | 'type' | 'step'> & UnitOverrideProps & {
  kind: MeasurementKind
  valueMetric?: number | null
  onValueMetricChange: (valueMetric: number | null) => void
  emptyValue?: number | null
}

export const MeasurementInput = ({
  kind,
  valueMetric,
  onValueMetricChange,
  emptyValue = null,
  unit: unitOverride,
  className,
  onBlur,
  onFocus,
  ...props
}: MeasurementInputProps) => {
  const measurements = useMeasurements()
  const unit = normalizeMeasurementUnit(unitOverride ?? measurements.unit)
  const formattedValue = formatMeasurementInputValue(valueMetric, kind, unit)
  const [draft, setDraft] = useState(() => formattedValue)
  const [isFocused, setIsFocused] = useState(false)
  const inputValue = isFocused ? draft : formattedValue

  return (
    <Input
      {...props}
      type="text"
      inputMode="decimal"
      step={inputStepFor(kind, unit)}
      className={className}
      value={inputValue}
      onFocus={(event) => {
        setDraft(formattedValue)
        setIsFocused(true)
        onFocus?.(event)
      }}
      onBlur={(event) => {
        setIsFocused(false)
        const parsed = parseMeasurementInput(draft, kind, unit)
        setDraft(parsed === null ? '' : formatMeasurementInputValue(parsed, kind, unit))
        onBlur?.(event)
      }}
      onChange={(event) => {
        const nextDraft = event.target.value
        setDraft(nextDraft)
        const parsed = parseMeasurementInput(nextDraft, kind, unit)
        onValueMetricChange(parsed === null ? emptyValue : parsed)
      }}
    />
  )
}

export type MeasurementFieldProps = MeasurementInputProps & {
  label: string
  labelClassName?: string
  wrapperClassName?: string
}

export const MeasurementField = ({
  label,
  labelClassName,
  wrapperClassName,
  id,
  kind,
  unit: unitOverride,
  ...inputProps
}: MeasurementFieldProps) => {
  const measurements = useMeasurements()
  const unit = normalizeMeasurementUnit(unitOverride ?? measurements.unit)

  return (
    <label className={cn('space-y-1', wrapperClassName)}>
      <span className={cn('text-sm font-medium text-foreground', labelClassName)}>
        {labelWithUnit(label, kind, unit)}
      </span>
      <MeasurementInput id={id} kind={kind} unit={unit} {...inputProps} />
    </label>
  )
}

export const MeasurementUnitSuffix = ({
  kind,
  unit: unitOverride,
  className
}: UnitOverrideProps & { kind: MeasurementKind; className?: string }) => {
  const measurements = useMeasurements()
  const unit = normalizeMeasurementUnit(unitOverride ?? measurements.unit)

  return <span className={className}>{unitLabelFor(kind, unit)}</span>
}
